import { motion } from "framer-motion";
import { Download, Sparkles, ArrowUpRight } from "lucide-react";
import BgLayer from "@/components/ui-shared/BgLayer";

const Hero = ({ onOpenCatalogue }) => {
    return (
        <section data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 overflow-hidden">
            <BgLayer variant="mesh" intensity="strong" />
            <div className="absolute inset-0 hero-grid opacity-70 pointer-events-none" />

            <div className="sxe-container relative">
                <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
                    {/* Left column */}
                    <div>
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="sxe-chip"
                            data-testid="hero-eyebrow"
                        >
                            <Sparkles className="w-3 h-3" />
                            AI-native Enterprise Learning Experience Platform
                        </motion.div>

                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.05 }}
                            className="font-display font-medium text-white mt-6 text-5xl md:text-6xl lg:text-[76px] leading-[1.02] tracking-tighter"
                            data-testid="hero-headline"
                        >
                            <span className="text-gradient-soft">Xelerating Careers.</span>{" "}
                            <br className="hidden md:block" />
                            <span className="text-gradient">Building Futures.</span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.15 }}
                            className="mt-6 text-[#B0A3C4] text-lg md:text-xl leading-relaxed max-w-xl"
                            data-testid="hero-subheadline"
                        >
                            SpringXE unifies learning, labs, assessments, and talent orchestration on one AI-native platform — so enterprises can build the workforce their future demands.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.25 }}
                            className="mt-9 flex flex-col sm:flex-row gap-3"
                        >
                            <button
                                type="button"
                                onClick={onOpenCatalogue}
                                data-testid="hero-cta-catalogue"
                                className="btn-primary"
                            >
                                Download Catalogue <Download className="w-4 h-4" />
                            </button>
                            <a href="#contact" data-testid="hero-cta-experts" className="btn-secondary">
                                Talk to Our Experts <ArrowUpRight className="w-4 h-4" />
                            </a>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-8 flex flex-wrap items-center gap-5 text-[11px] uppercase tracking-widest text-[#8f7fb0] font-display"
                        >
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#C266FF] animate-pulse-glow" />
                                SOC 2 Aligned
                            </span>
                            <span>GDPR + DPDP Ready</span>
                            <span>Enterprise SSO</span>
                            <span>500+ Enterprises</span>
                        </motion.div>
                    </div>

                    {/* Right column visual — full enterprise image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.9 }}
                        className="relative rounded-3xl overflow-hidden border border-[#C266FF]/25 shadow-[0_30px_80px_-30px_rgba(161,0,255,0.5)]"
                    >
                        <img
                            src="https://images.unsplash.com/photo-1573164574572-cb89e39749b4?auto=format&fit=crop&w=1200&q=75"
                            alt="Enterprise learning team"
                            className="w-full h-[560px] object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-[#07020F] via-[#07020F]/10 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#07020F]/60" />

                        {/* Floating badge */}
                        <div className="absolute top-5 left-5 sxe-glass rounded-full px-4 py-2 border border-[#C266FF]/30 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C266FF] animate-pulse-glow" />
                            <span className="font-display text-[11px] uppercase tracking-widest text-white">Live cohort · GenAI</span>
                        </div>

                        {/* Bottom stat card */}
                        <div className="absolute bottom-5 left-5 right-5 sxe-glass rounded-2xl border border-white/[0.08] p-4 flex items-center justify-between">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-[#75668C] font-display">Enterprise readiness</div>
                                <div className="font-display text-white text-lg font-medium tracking-tight mt-0.5">+38% skill velocity</div>
                            </div>
                            <div className="flex -space-x-2">
                                {["A", "R", "V", "S"].map((c) => (
                                    <div key={c} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center text-white text-[11px] font-display font-semibold border-2 border-[#0d0621]">
                                        {c}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
